import { LinkProps } from "@tanstack/react-router"
import { createColumnHelper, flexRender, Header, useTable } from "@tanstack/react-table"
import { Email } from "design-system/atoms/Email"
import { Label } from "design-system/atoms/Label"
import { Phone } from "design-system/atoms/Phone"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { features, type Features } from "lib/table"
import { CompanyClientCell } from "modules/customers/components/CompanyClientCell"
import { CustomerTagsCell } from "modules/customers/components/List/CustomerTagsCell"
import { useCustomersQuery } from "modules/customers/hooks/useCustomersQuery"
import { useIsAmphoraCompany } from "modules/customers/hooks/useIsAmphoraCompany"
import { CustomerLocale } from "modules/shared/components/Miscelanea/CustomerLocale"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CustomerListSearchSchema } from "routes/_base/_auth/customers/list/route"
import { CustomersResponseDataSchema, customersSortFields } from "schema"
import { z } from "zod"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof CustomersResponseDataSchema>[number]
>()

type CustomersQueryReturn = ReturnType<typeof useCustomersQuery>

type CustomersSortFields = (typeof customersSortFields)[number]

type Props = {
  customers: CustomersQueryReturn["data"]
  isPending: CustomersQueryReturn["isPending"]
  fields: CustomersSortFields[]
  onSort: (field: CustomersSortFields) => void
  search: z.infer<typeof CustomerListSearchSchema>
  navigate?: (customer: z.infer<typeof CustomersResponseDataSchema>[number]) => LinkProps
}

export const CustomersTable = ({
  customers,
  isPending,
  fields,
  onSort,
  search,
  navigate,
}: Props) => {
  const { t } = useTranslation()
  const isAmphoraCompany = useIsAmphoraCompany()

  const columns = useMemo(
    () =>
      columnHelper.columns(
        [
          columnHelper.accessor("name", {
            header: t("customers.table.name"),
            meta: {
              colSpan: 2,
            },
            cell: (info) => {
              const row = info.row.original
              const isEmphasized = !!row.name?.length

              return (
                <Label className={cn(!isEmphasized && "font-normal text-muted-foreground")}>
                  {row.name || row.tentativeName || t("customers.profile.noName")}
                </Label>
              )
            },
          }),
          isAmphoraCompany &&
            columnHelper.accessor("client", {
              id: "client",
              header: t("customers.table.client"),
              cell: ({ row }) => <CompanyClientCell client={row.original.client} />,
            }),
          columnHelper.accessor("phone", {
            header: t("customers.table.phone"),
            cell: (info) => {
              const row = info.row.original
              const isEmphasized = !!row.phone?.length
              const phone = row.phone || row.tentativePhone

              return (
                <Phone className={cn(!isEmphasized && "text-muted-foreground")} phone={phone} />
              )
            },
          }),
          columnHelper.accessor("email", {
            header: t("customers.table.email"),
            cell: (info) => {
              const row = info.row.original
              const isEmphasized = !!row.email?.length

              return (
                <Email
                  className={cn(!isEmphasized && "text-muted-foreground")}
                  email={row.email || row.tentativeEmail}
                />
              )
            },
          }),
          columnHelper.accessor("locale", {
            header: t("customers.table.locale"),
            cell: ({ row }) => <CustomerLocale {...row.original} />,
          }),
          columnHelper.accessor("tags", {
            header: t("customers.table.tags"),
            cell: ({ getValue }) => <CustomerTagsCell tags={getValue() ?? []} />,
          }),
          columnHelper.accessor("lastMessageSentAt", {
            header: t("customers.table.lastMessageSentAt"),
            cell: ({ getValue }) => {
              const value = getValue()
              return value ? <DateDisplay date={value} /> : "-"
            },
            meta: { align: "right" },
          }),
          columnHelper.accessor("messagesCount", {
            header: t("customers.table.messagesCount"),
            cell: (info) => <span className="font-mono">{info.getValue()}</span>,
            meta: { align: "right" },
          }),
        ].filter(Boolean),
      ),
    [isAmphoraCompany, t],
  )

  const table = useTable({
    data: customers ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || customers === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (customers.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("customers.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.original.id}
        navigate={
          navigate
            ? navigate(row.original)
            : ({
                to: "/customers/list/$id",
                params: {
                  id: row.original.id,
                },
                search: {
                  ...search,
                  lastRow: row.id,
                  tab: (search as { tab?: string }).tab || "customer",
                },
              } as LinkProps)
        }
        className="group"
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            className={cn(
              cell.column.columnDef.meta?.className,
              "lastRow" in search && String(search.lastRow) === row.id && "bg-muted",
            )}
            {...cell.column.columnDef.meta}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  function buildSortable(
    header: Header<Features, z.infer<typeof CustomersResponseDataSchema>[number], unknown>,
  ) {
    if (!fields.includes(header.id as CustomersSortFields)) return

    const isActive = search.field === header.id

    return {
      active: isActive,
      direction: isActive ? search.order : "asc",
      onClick: () => onSort(header.id as CustomersSortFields),
    }
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                sortable={buildSortable(header)}
                {...header.column.columnDef.meta}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody className="**:data-[slot=table-cell]:first:w-8">{renderContent()}</TableBody>
    </Table>
  )
}
